import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface IActionsVisibilityContextState {
  actionsVisible: boolean;
  showActions: () => void;
  hideActions: () => void;
  blurActions: (event: React.FocusEvent<HTMLElement>) => void;
}

const initialState: IActionsVisibilityContextState = {
  actionsVisible: false,
  showActions: () => null,
  hideActions: () => null,
  blurActions: () => null,
};

interface IActionsVisibilityContextProviderProps {
  children: ReactNode;
}

const ActionsVisibilityContext = createContext(initialState);

export const useActionsVisibilityContext = () =>
  useContext(ActionsVisibilityContext);

export const ActionsVisibilityProvider = ({
  children,
}: IActionsVisibilityContextProviderProps) => {
  const timeoutIdRef = useRef<NodeJS.Timeout | null>();

  const [actionsVisible, setActionsVisible] = useState(false);

  const showActions = useCallback(() => setActionsVisible(true), []);
  const hideActions = useCallback(() => setActionsVisible(false), []);

  // Inherit visibility from parent
  const { actionsVisible: parentActionsVisible } =
    useActionsVisibilityContext();

  // clearing the timeout
  useEffect(() => {
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, []);

  const blurActions = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      // delay execution to allow potential click events to be processed first
      timeoutIdRef.current = setTimeout(() => {
        // checks if the focus has moved away from the current element to another element.
        if (document.activeElement !== event.currentTarget) {
          hideActions();
        }
      }, 300);
    },
    [hideActions],
  );

  const contextValue: IActionsVisibilityContextState = useMemo(
    () => ({
      actionsVisible: parentActionsVisible || actionsVisible,
      showActions,
      hideActions,
      blurActions,
    }),
    [
      actionsVisible,
      blurActions,
      hideActions,
      parentActionsVisible,
      showActions,
    ],
  );

  return (
    <ActionsVisibilityContext.Provider value={contextValue}>
      {children}
    </ActionsVisibilityContext.Provider>
  );
};

export function withActionsVisibilityContext<T extends object>(
  WrappedComponent: React.ComponentType<T>,
): React.FC<T> {
  // displayName is used for React Dev Tools.
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithContext = (props: T) => {
    return (
      <ActionsVisibilityProvider>
        <WrappedComponent {...props} />
      </ActionsVisibilityProvider>
    );
  };

  ComponentWithContext.displayName = `withActionsVisibilityContext(${displayName})`;
  return ComponentWithContext;
}
